import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { employeesAPI, hrDocsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, FileText, Download, Eye, FileSignature } from 'lucide-react';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Field map: which form fields each document type needs
const TYPE_FIELDS = {
  experience_letter: ['last_working_day'],
  relieving_letter: ['last_working_day'],
  promotion_letter: ['new_position', 'new_department', 'new_salary', 'effective_date'],
  bonus_letter: ['bonus_amount', 'bonus_reason', 'effective_date'],
  warning_letter: ['warning_level', 'warning_reason'],
  confirmation_letter: ['confirmation_date'],
};

const FIELD_DEFS = {
  last_working_day: { label: 'Last Working Day *', type: 'date' },
  new_position: { label: 'New Position *', type: 'text', placeholder: 'Senior Manager' },
  new_department: { label: 'New Department', type: 'text', placeholder: 'Operations' },
  new_salary: { label: 'New Annual Salary', type: 'number', placeholder: '180000' },
  effective_date: { label: 'Effective Date *', type: 'date' },
  bonus_amount: { label: 'Bonus Amount *', type: 'number', placeholder: '5000' },
  bonus_reason: { label: 'Reason', type: 'text', placeholder: 'Q1 outstanding performance' },
  warning_level: { label: 'Warning Level', type: 'select', options: ['verbal', 'written', 'final'] },
  warning_reason: { label: 'Reason / Details *', type: 'textarea', placeholder: 'Describe the incident or behaviour…' },
  confirmation_date: { label: 'Confirmation Date *', type: 'date' },
};

const HRDocumentsHub = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [docType, setDocType] = useState('experience_letter');
  const [employeeId, setEmployeeId] = useState('');
  const [extras, setExtras] = useState({});
  const [useCustom, setUseCustom] = useState(false);
  const [customBody, setCustomBody] = useState('');
  const [preview, setPreview] = useState(null);

  const allowedRole = !user || ['super_admin', 'hr_admin'].includes(user?.role);
  const fields = useMemo(() => TYPE_FIELDS[docType] || [], [docType]);

  const fetchAll = async () => {
    try {
      const [e, t, h] = await Promise.all([employeesAPI.getAll(), hrDocsAPI.types(), hrDocsAPI.list()]);
      setEmployees(e.data);
      setTypes(t.data);
      // Only show the 6 HR doc types in history (not offer/salary cert)
      const hrTypes = new Set(t.data.map(x => x.document_type));
      setHistory(h.data.filter(d => hrTypes.has(d.document_type)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (allowedRole) fetchAll(); /* eslint-disable-next-line */ }, [allowedRole]);

  const onTypeChange = (v) => { setDocType(v); setExtras({}); setUseCustom(false); setCustomBody(''); setPreview(null); };

  const generate = async (download) => {
    if (!employeeId) { toast.error('Select an employee'); return; }
    setGenerating(true);
    try {
      const payload = { document_type: docType, employee_id: employeeId };
      if (useCustom) payload.custom_body = customBody;
      Object.entries(extras).forEach(([k, v]) => {
        if (v !== '' && v != null) payload[k] = FIELD_DEFS[k]?.type === 'number' ? parseFloat(v) : v;
      });
      const r = await hrDocsAPI.generateHR(payload);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const emp = employees.find(e => e.id === employeeId);
      const name = emp ? `${emp.first_name}_${emp.last_name}` : 'employee';
      if (download) downloadBlob(blob, `${docType}_${name}.pdf`);
      else setPreview(URL.createObjectURL(blob));
      toast.success('Generated');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const downloadHistory = async (id, filename) => {
    try {
      const r = await hrDocsAPI.download(id);
      downloadBlob(new Blob([r.data], { type: 'application/pdf' }), filename);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowedRole) return <Navigate to="/" replace />;

  const currentTypeLabel = types.find(t => t.document_type === docType)?.label || docType;

  return (
    <div className="space-y-6" data-testid="hr-docs-hub">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileSignature />HR Document Templates</h1>
        <p className="text-muted-foreground">Experience, Relieving, Promotion, Bonus, Warning, Confirmation — all using the company letterhead</p>
      </div>

      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate" data-testid="tab-generate">Generate</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Document Type *</Label>
                  <Select value={docType} onValueChange={onTypeChange}>
                    <SelectTrigger data-testid="hr-doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {types.map(t => <SelectItem key={t.document_type} value={t.document_type}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employee *</Label>
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger data-testid="hr-doc-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {!useCustom && fields.map(name => {
                  const def = FIELD_DEFS[name];
                  if (!def) return null;
                  if (def.type === 'select') {
                    return (
                      <div key={name}>
                        <Label>{def.label}</Label>
                        <Select value={extras[name] || ''} onValueChange={(v) => setExtras({...extras, [name]: v})}>
                          <SelectTrigger data-testid={`field-${name}`}><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{def.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  if (def.type === 'textarea') {
                    return (
                      <div key={name}>
                        <Label>{def.label}</Label>
                        <Textarea value={extras[name] || ''} onChange={(e) => setExtras({...extras, [name]: e.target.value})} rows={3} placeholder={def.placeholder} data-testid={`field-${name}`} />
                      </div>
                    );
                  }
                  return (
                    <div key={name}>
                      <Label>{def.label}</Label>
                      <Input type={def.type} value={extras[name] || ''} onChange={(e) => setExtras({...extras, [name]: e.target.value})} placeholder={def.placeholder} data-testid={`field-${name}`} />
                    </div>
                  );
                })}

                <div className="flex items-center gap-2 pt-2 border-t">
                  <input type="checkbox" id="usecustom" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} data-testid="use-custom" />
                  <Label htmlFor="usecustom" className="cursor-pointer">Override body with custom text (overrides template)</Label>
                </div>
                {useCustom && (
                  <Textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)} rows={8} placeholder="Enter the full body. Separate paragraphs with a blank line. Letterhead and signature are auto-applied." data-testid="custom-body" />
                )}

                <div className="bg-muted/40 rounded p-3 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Auto-filled from employee + company:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>Name, employee code, designation, department, joining date, salary</li>
                    <li>Company name, address, letterhead, logo, signatory, signature & stamp</li>
                  </ul>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => generate(false)} disabled={generating} data-testid="hr-doc-preview-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Eye size={14} className="mr-2" />}Preview
                  </Button>
                  <Button onClick={() => generate(true)} disabled={generating} data-testid="hr-doc-download-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Preview — {currentTypeLabel}</CardTitle></CardHeader>
              <CardContent>
                {preview ? (
                  <iframe src={preview} className="w-full" style={{ height: 600 }} title="preview" data-testid="hr-doc-preview-iframe" />
                ) : (
                  <div className="border-2 border-dashed rounded p-12 text-center text-muted-foreground">
                    <FileText className="mx-auto mb-2" />
                    Pick an employee and click <strong>Preview</strong>.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Type</TableHead><TableHead>Employee</TableHead>
                <TableHead>Generated</TableHead><TableHead>By</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No HR documents generated yet</TableCell></TableRow>}
                {history.map(d => (
                  <TableRow key={d.id} data-testid={`history-row-${d.id}`}>
                    <TableCell><Badge variant="outline">{types.find(t => t.document_type === d.document_type)?.label || d.document_type}</Badge></TableCell>
                    <TableCell className="font-medium">{d.employee_name || '—'}<div className="text-xs text-muted-foreground">{d.employee_code}</div></TableCell>
                    <TableCell className="text-xs">{new Date(d.generated_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{d.generated_by}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => downloadHistory(d.id, d.filename)} data-testid={`download-${d.id}`}>
                        <Download size={12} className="mr-1" />PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HRDocumentsHub;
